import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AvaliacaoResultado from '../components/calculator/AvaliacaoResultado';
import OtpAuth from '../components/OtpAuth';
import { buildSavedSnapshot } from '../lib/resultSnapshot';
import { isSavedToken, readAnonymousHandoff, savedAssessmentIdFromToken } from '../lib/resultHandoff';

/**
 * Página de interpretação completa (/avaliacao/:token).
 *
 * Não é indexável e não deve ser encontrada por URL adivinhada: o token é um UUID
 * aleatório (avaliação anônima, guardado só em sessionStorage desta aba, nunca enviado
 * a um servidor) ou "s-<id>" (avaliação já salva no histórico, atrás de autenticação —
 * `GET /api/management/assessments/:id` confirma a posse no servidor).
 *
 * Não chama useSEO: a casca estática desta rota (scripts/prerender.mjs) já sai do build
 * com "noindex, nofollow", mais estrito do que o "noindex, follow" que o hook aplicaria.
 */
async function fetchSavedAssessment(id) {
  const response = await fetch(`/api/management/assessments/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  const payload = await response.json().catch(() => ({}));
  if (response.status === 401) return { status: 'login' };
  if (!response.ok) return { status: 'error', message: payload.error || 'Não foi possível carregar a avaliação.' };
  return { status: 'ready', snapshot: buildSavedSnapshot(payload.assessment) };
}

export default function Avaliacao() {
  const { token } = useParams();
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ status: 'loading', snapshot: null, message: '' });

  useEffect(() => {
    document.title = 'Interpretação da avaliação | MiniMental.com.br';
  }, []);

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading', snapshot: null, message: '' });

    if (isSavedToken(token)) {
      window.eidocAnalytics?.capture('assessment_interpretation_opened', { source: 'saved' });
      fetchSavedAssessment(savedAssessmentIdFromToken(token)).then((result) => {
        if (alive) setState({ status: result.status, snapshot: result.snapshot || null, message: result.message || '' });
      });
    } else {
      const snapshot = readAnonymousHandoff(token);
      if (snapshot) window.eidocAnalytics?.capture('assessment_interpretation_opened', { source: 'local' });
      setState(snapshot ? { status: 'ready', snapshot, message: '' } : { status: 'expired', snapshot: null, message: '' });
    }

    return () => { alive = false; };
  }, [token, retry]);

  if (state.status === 'loading') {
    return (
      <main className="mx-auto min-h-[60vh] max-w-[760px] px-4 py-16">
        <p role="status" className="text-[var(--text-muted)]">Carregando avaliação…</p>
      </main>
    );
  }

  if (state.status === 'login') {
    return (
      <main className="mx-auto min-h-[60vh] max-w-md px-4 py-16">
        <h1 className="text-2xl font-semibold text-[var(--primary)]">Entre para ver esta avaliação</h1>
        <p className="mt-2 text-[var(--text-muted)]">Esta avaliação está guardada no histórico de um paciente. Confirme seu e-mail para abri-la.</p>
        <div className="mt-5"><OtpAuth onAuthenticated={() => setRetry((n) => n + 1)} /></div>
      </main>
    );
  }

  if (state.status === 'expired' || state.status === 'error') {
    return (
      <main className="mx-auto min-h-[60vh] max-w-md px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold text-[var(--primary)]">
          {state.status === 'expired' ? 'Esta sessão expirou' : 'Não foi possível abrir esta avaliação'}
        </h1>
        <p className="mt-2 text-[var(--text-muted)]">
          {state.message || 'O resultado detalhado fica disponível só por um tempo limitado neste navegador, por privacidade. Refaça o cálculo para ver a interpretação completa.'}
        </p>
        <Link to="/" className="btn-primary mt-6 inline-flex">Ir para a calculadora</Link>
      </main>
    );
  }

  return <AvaliacaoResultado snapshot={state.snapshot} />;
}
